import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { api } from './api'
import type {
  OreasAwaiting,
  OreasCatalogue,
  OreasClientChoice,
  OreasIssuerChoice,
  OreasPayload,
  OreasProduct,
  OreasRequest,
  OreasStockChange,
  OreasValue,
  OreasValueKind,
  OreasValues,
  resolvePayload,
} from './oreas'

const STAFF_PATH = '/api/inventory/oreas'

export function useOreasCatalogue() {
  return useQuery({
    queryKey: ['inventory', 'oreas'],
    queryFn: async () => (await api.get<OreasCatalogue>(STAFF_PATH)).data,
  })
}

export function useSaveOreasProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string | null; payload: OreasPayload }) =>
      id
        ? (await api.patch<OreasProduct>(`${STAFF_PATH}/${id}`, payload)).data
        : (await api.post<OreasProduct>(STAFF_PATH, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas'] }),
  })
}

export function useOreasValues() {
  return useQuery({
    queryKey: ['inventory', 'oreas', 'values'],
    queryFn: async () => (await api.get<OreasValues>(`${STAFF_PATH}/values`)).data,
  })
}

export function useAddOreasValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ kind, label }: { kind: OreasValueKind; label: string }) =>
      (await api.post<OreasValue>(`${STAFF_PATH}/values/${kind}`, { label })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas', 'values'] }),
  })
}

export function useRenameOreasValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ kind, current, label }: { kind: OreasValueKind; current: string; label: string }) =>
      (await api.patch<OreasValue>(`${STAFF_PATH}/values/${kind}`, { current, label })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas'] }),
  })
}

export function useRemoveOreasValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ kind, label }: { kind: OreasValueKind; label: string }) => {
      await api.delete(`${STAFF_PATH}/values/${kind}`, { params: { label } })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas', 'values'] }),
  })
}

export function useDeleteOreasProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${STAFF_PATH}/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas'] }),
  })
}

const REQUESTS_KEY = ['inventory', 'oreas', 'requests']

export function useOreasRequests(status: string | null, enabled = true) {
  return useQuery({
    queryKey: [...REQUESTS_KEY, status],
    queryFn: async () =>
      (await api.get<OreasRequest[]>(`${STAFF_PATH}/requests`, { params: status ? { status } : undefined })).data,
    enabled,
  })
}

export function useOreasWaiting(enabled: boolean) {
  return useQuery({
    queryKey: [...REQUESTS_KEY, 'waiting'],
    queryFn: async () => (await api.get<OreasRequest[]>(`${STAFF_PATH}/requests/waiting`)).data,
    enabled,
  })
}

/** The sidebar's number; any mutation settling asks again, because voiding a document in Sales changes what waits here. */
export function useOreasAwaiting(enabled: boolean): number {
  const queryClient = useQueryClient()
  const mutating = useIsMutating()
  const wasMutating = useRef(mutating)
  useEffect(() => {
    if (enabled && wasMutating.current > 0 && mutating === 0) {
      void queryClient.invalidateQueries({ queryKey: REQUESTS_KEY })
    }
    wasMutating.current = mutating
  }, [mutating, queryClient, enabled])

  const { data } = useQuery({
    queryKey: [...REQUESTS_KEY, 'awaiting'],
    queryFn: async () => (await api.get<OreasAwaiting>(`${STAFF_PATH}/requests/awaiting`)).data,
    enabled,
    refetchOnWindowFocus: true,
  })
  return enabled ? (data?.count ?? 0) : 0
}

export function useOreasClientChoices(requestId: string, search: string) {
  return useQuery({
    queryKey: ['inventory', 'oreas', 'requests', requestId, 'clients', search],
    queryFn: async () =>
      (
        await api.get<OreasClientChoice[]>(`${STAFF_PATH}/requests/${requestId}/clients`, {
          params: search.trim() ? { search: search.trim() } : undefined,
        })
      ).data,
  })
}

export function useOreasIssuers() {
  return useQuery({
    queryKey: ['inventory', 'oreas', 'issuers'],
    queryFn: async () => (await api.get<OreasIssuerChoice[]>(`${STAFF_PATH}/requests/issuers`)).data,
  })
}

export function useOreasStockHistory(productId: string) {
  return useQuery({
    queryKey: ['inventory', 'oreas', 'history', productId],
    queryFn: async () => (await api.get<OreasStockChange[]>(`${STAFF_PATH}/${productId}/history`)).data,
  })
}

export function useResolveOreasRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ReturnType<typeof resolvePayload> }) =>
      (await api.post<OreasRequest>(`${STAFF_PATH}/requests/${id}/resolve`, payload)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas'] }),
  })
}

export function useDecideOreasRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'reject' | 'close' }) =>
      (await api.post<OreasRequest>(`${STAFF_PATH}/requests/${id}/${action}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas'] }),
  })
}

export function useAnswerOreasLine() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      lineId,
      action,
      body,
    }: {
      lineId: string
      action: 'accept' | 'decline' | 'give-back' | 'keep'
      body?: { quantity: number }
    }) => (await api.post<OreasRequest>(`${STAFF_PATH}/request-lines/${lineId}/${action}`, body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'oreas'] }),
  })
}
